/**
 * DetailViewModal — карточка строки (Generation и Search).
 *
 * M2-B (rev 3-4): сырые enriched_data / raw_data / generated JSON — НЕ
 * user-facing в Search-режиме. v2-карточка (schema_version=2,
 * engine=search_v2) — вкладки «Результат» (карточка базы истины или
 * SafeResultFallback: статус/источники/summary/причины человекочитаемо)
 * и «Детали» (источники/прогоны/причины). Любой не-v2 payload — только
 * безопасное сообщение, без дампов. Raw data и generated content
 * показываются только в Generation-режиме (явные пропы showRawData /
 * showGeneratedContent); вкладка «Лог», промпты и ответы LLM удалены.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import api from '@/lib/api';
import type { PipelineRow } from '@/hooks/useGeneration';
import type { SearchRowResult } from '@/hooks/useSearchResults';
import type { V2Enriched } from '@/components/search/SearchDetailSources';
import {
  SearchItemCardView,
  type SearchItemCard,
} from '@/components/search/SearchItemCardView';
import {
  SearchItemDetailsView,
  type SearchItemDetails,
} from '@/components/search/SearchItemDetailsView';
import { Modal } from '@/components/ui/Modal';
import { statusLabel } from '@/components/search/reasonLabel';
import { cn } from '@/lib/utils';

// M2-B rev 3 (2026-09-15): search_log/synthesis_log/debug/промпты/сырой JSON —
// НЕ user-facing (ни для новых, ни для legacy-результатов). Сырой enriched_data
// из модалки удалён полностью; v2-карточка рендерится из базы истины,
// raw_data — только в Generation (showRawData).
//
// Fix 2026-09-18 (дефект №5): без truth-карточки enriched_data НЕ показывается
// вообще (SafeResultFallback удалён) — только «Результат ещё не сохранён».
// Данные последнего запуска — это чужие/сырые данные другого контракта:
// цена/наличие/упаковка и т.п. (дефект №6) не должны попасть в карточку.

function _isSearchV2Payload(value: unknown): value is V2Enriched {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return v.schema_version === 2 && v.engine === 'search_v2';
}

export function DetailViewModal({
  row,
  onClose,
  legacyResult = false,
  truthResult = null,
  showRawData = true,
  showGeneratedContent = true,
}: {
  row: PipelineRow;
  onClose: () => void;
  /** M2-B: truth-строка с legacy_result=true (есть enriched_data, нет truth-item).
   *  Пользователю — только безопасный статус, никакой технической выдачи. */
  legacyResult?: boolean;
  /** Fix 2026-09-18 (дефект №5): truth-статус строки из /search/results —
   *  различает тестовый прогон (результат не сохраняется по определению) и
   *  полный прогон с несохранённым результатом. */
  truthResult?: SearchRowResult | null;
  /** M2-B rev 3: сырой raw_data показывается только в Generation-режиме
   *  (дефолт true — Generation не меняется). Search передаёт false:
   *  raw data в Search-карточке не показывается. Режим — явный проп,
   *  не вывод по косвенным признакам. */
  showRawData?: boolean;
  /** M2-B rev 4: JSON generated content — только Generation-контракт
   *  (дефолт true). Search передаёт false: generated JSON не показывается.
   *  Режим — явный проп. */
  showGeneratedContent?: boolean;
}) {
  const { t } = useTranslation();
  const isSearchV2 = _isSearchV2Payload(row.enriched_data);
  // Fix 2026-09-18 (дефект №5): смысловой статус строки без truth-записи.
  const isTestRunRow = Boolean(truthResult?.test_run_result && !truthResult.status);
  // TZ 2026-09-20 (ТЗ §3): тестовый результат — это ПОЛНОЦЕННАЯ карточка
  // превью (kind='test' от API); предупреждение о предварительности ниже.
  const isTestPreview = truthResult
    ? truthResult.current_display_result_kind === 'test'
    : isTestRunRow;
  // M2-A/M2-B: вкладки только для search-v2 payload (иначе — безопасное
  // сообщение). «Результат» — карточка из базы истины (или SafeResultFallback);
  // «Детали» — источники/прогоны/причины. Вкладки «Лог» больше нет:
  // search_log/synthesis_log/промпты/ответы LLM — не user-facing.
  const [tab, setTab] = useState<'result' | 'details'>('result');
  const [card, setCard] = useState<SearchItemCard | null>(null);
  const [details, setDetails] = useState<SearchItemDetails | null>(null);
  const [cardLoading, setCardLoading] = useState(false);

  useEffect(() => {
    if (!isSearchV2) return;
    let cancelled = false;
    setCardLoading(true);
    setCard(null);
    setDetails(null);
    api
      .get<{ card: SearchItemCard | null; details: SearchItemDetails | null }>(
        `/search/row/${row.id}/result`,
      )
      .then((r) => {
        if (cancelled) return;
        setCard(r.data.card);
        setDetails(r.data.details);
      })
      .catch(() => {
        // База истины недоступна — остаёмся на fallback-рендере enriched_data.
      })
      .finally(() => {
        if (!cancelled) setCardLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSearchV2, row.id]);

  return (
    <Modal open={true} onClose={onClose} maxWidth="2xl">
      <div className="card max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {t('search.detail.rowNumber', { index: row.row_index, defaultValue: `Row #${row.row_index}` })}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Status + error */}
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded bg-surface-800">
            {statusLabel(t, row.status)}
          </span>
          {legacyResult && (
            <span className="text-xs px-2 py-0.5 rounded bg-warning-tint/15 text-warning">
              {t('search.status.legacyResult', { defaultValue: 'Legacy-format result' })}
            </span>
          )}
          {isTestPreview && (
            <span className="text-xs px-2 py-0.5 rounded bg-info-tint/15 text-info">
              {t('search.status.testResult', { defaultValue: 'Test result' })}
            </span>
          )}
          {row.error_message && (
            <span className="text-xs text-danger">{row.error_message}</span>
          )}
        </div>

        {/* Generated content — только Generation-режим (showGeneratedContent,
            дефолт true); Search-модалка generated JSON не показывает (M2-B rev 4). */}
        {showGeneratedContent && row.generated_content && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-2 text-surface-300">
              {t('search.detail.generated', { defaultValue: 'Generated' })}
            </h3>
            {Object.entries(row.generated_content).map(([moduleKey, payload]) => (
              <div key={moduleKey} className="mb-4">
                <div className="text-xs text-primary-300 mb-1">{moduleKey}</div>
                <pre className="bg-surface-800/40 rounded-lg p-3 text-xs overflow-x-auto">
                  {JSON.stringify(payload, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}

        {/* Search enrichment — v2 EnrichmentResult (result.model_dump() flat).
            Для search-v2: вкладки «Результат» / «Детали» (карточка базы
            истины, без логов/промптов); любой не-v2 payload — только
            безопасное сообщение (M2-B rev 3-4). */}
        {row.enriched_data && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold mb-2 text-surface-300">
              {t('search.detail.searchEnrichment', { defaultValue: 'Search enrichment' })}
            </h3>
            {isSearchV2 ? (
              <>
                <div className="flex items-center gap-1 mb-3">
                  {(
                    [
                      ['result', 'search.log.tabResult'],
                      ['details', 'search.log.tabDetails'],
                    ] as const
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      className={cn(
                        'text-xs px-3 py-1.5 rounded-lg uppercase tracking-wider transition-colors',
                        tab === key
                          ? 'bg-surface-700/60 text-surface-100'
                          : 'text-surface-500 hover:text-surface-300',
                      )}
                    >
                      {t(label, { defaultValue: key })}
                    </button>
                  ))}
                </div>
                {tab === 'result' ? (
                  card ? (
                    <>
                      {isTestPreview && (
                        <div className="mb-3 rounded-lg border border-info-tint/30 bg-info-tint/10 px-3 py-2">
                          <div className="text-sm font-medium text-info">
                            {t('search.card.testResultTitle', {
                              defaultValue: 'Test result',
                            })}
                          </div>
                          <div className="text-xs text-info mt-0.5">
                            {t('search.card.testResultWarning', {
                              defaultValue:
                                'This is a preview. It does not replace the permanent result of a full run.',
                            })}
                          </div>
                        </div>
                      )}
                      <SearchItemCardView card={card} />
                    </>
                  ) : (
                    // Fix 2026-09-18 (дефект №5): без сохранённого результата —
                    // ТОЛЬКО сообщение; данные последнего запуска (безопасный
                    // fallback) не показываются — в них были чужие коммерческие
                    // данные другого контракта (дефект №6).
                    cardLoading ? (
                      <p className="text-sm text-surface-500">…</p>
                    ) : (
                    <p className="text-sm text-surface-400">
                      {/* TZ 2026-09-20 (ТЗ §1/§5): без завершённого запуска —
                          «Не запускался»; текстов «Результат не сохранён» нет. */}
                      {isTestPreview
                        ? t('search.card.testRunNote', {
                            defaultValue: 'Test run — no permanent result is created.',
                          })
                        : t('search.grid.notRun', { defaultValue: 'Not run' })}
                    </p>
                    )
                  )
                ) : tab === 'details' ? (
                  details ? (
                    <SearchItemDetailsView details={details} />
                  ) : (
                    <p className="text-sm text-surface-500">
                      {t('search.details.noRunsFor', {
                        defaultValue: 'No completed runs for this row.',
                      })}
                    </p>
                  )
                ) : null}
              </>
            ) : (
              // M2-B rev 3: любой не-v2 payload (legacy, unknown, техдамп) —
              // только безопасное сообщение; сырой enriched_data не показывается
              // ни в каком виде. Бейдж legacy-результата — в блоке статуса выше.
              <p className="text-xs text-surface-500">
                {t('search.card.noSavedDetails', {
                  defaultValue: 'Detailed card is not saved yet.',
                })}
              </p>
            )}
          </div>
        )}

        {/* Raw data — только Generation-режим (showRawData=true по умолчанию);
            Search-модалка raw_data не показывает (M2-B rev 3). */}
        {showRawData && row.raw_data && (
          <div>
            <h3 className="text-sm font-semibold mb-2 text-surface-300">
              {t('search.detail.rawData', { defaultValue: 'Raw data' })}
            </h3>
            <pre className="bg-surface-800/40 rounded-lg p-3 text-xs overflow-x-auto">
              {JSON.stringify(row.raw_data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Modal>
  );
}
